import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Product } from "@/lib/types";
import { getProductFromSlug, generateProductMetaTags } from "@/lib/seo-utils";
import { generateProductSchema } from "@/lib/structured-data";
import { useAuth } from "@/context/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, ArrowRight, Check } from "lucide-react";
import { ProductProcessFlow } from "@/components/ProductProcessFlow";

export default function ProductLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) {
        navigate("/");
        return;
      }

      const fetchedProduct = await getProductFromSlug(slug);

      if (!fetchedProduct || fetchedProduct.active === false) {
        // Product not found or inactive, redirect to homepage
        navigate("/");
        return;
      }

      setProduct(fetchedProduct);
      setLoading(false);
    };

    fetchProduct();
  }, [slug, navigate]);

  const handleCreateDesign = () => {
    if (!product) return;

    if (user) {
      // User is authenticated, go to wizard with product pre-selected
      navigate(`/projects/new?product=${product.id}`);
    } else {
      // User not authenticated, show auth dialog
      setIsAuthDialogOpen(true);
    }
  };

  // After successful auth (e.g. OAuth popup), navigate to wizard with product pre-selected
  useEffect(() => {
    if (user && product && isAuthDialogOpen) {
      setIsAuthDialogOpen(false);
      navigate(`/projects/new?product=${product.id}`);
    }
  }, [user, product, isAuthDialogOpen, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) return null;

  const metaTags = generateProductMetaTags(product);
  const structuredData = generateProductSchema(product);

  // Calculate price range for display
  const prices = product.variants.map(v => v.stripePrice);
  const lowestPrice = Math.min(...prices);
  const highestPrice = Math.max(...prices);
  const hasUniformPricing = lowestPrice === highestPrice;

  const priceDisplay = hasUniformPricing
    ? `$${lowestPrice.toFixed(2)}`
    : `$${lowestPrice.toFixed(2)} - $${highestPrice.toFixed(2)}`;

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        <meta name="keywords" content={metaTags.keywords} />

        {/* Open Graph tags */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={metaTags.ogTitle} />
        <meta property="og:description" content={metaTags.ogDescription} />
        <meta property="og:image" content={metaTags.ogImage} />
        <meta property="og:url" content={metaTags.ogUrl} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTags.twitterTitle} />
        <meta name="twitter:description" content={metaTags.twitterDescription} />
        <meta name="twitter:image" content={metaTags.twitterImage} />

        {/* Canonical URL */}
        <link rel="canonical" href={metaTags.ogUrl} />

        {/* Structured Data - Product Schema */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="container mx-auto px-4 py-12 md:py-20">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Product Image */}
              <div className="order-2 md:order-1">
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 p-8">
                  <img
                    src={product.thumbnail}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Product Info & CTA */}
              <div className="order-1 md:order-2 space-y-6">
                {/* Categories */}
                {product.categories && product.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {product.categories.map(category => (
                      <Badge key={category} variant="secondary">
                        {category}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Title */}
                <h1 className="text-h1 md:text-display font-bold tracking-tight">{product.name}</h1>

                {/* Description */}
                <p className="text-lg text-muted-foreground">{product.description}</p>

                {/* Price */}
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-accent">{priceDisplay}</span>
                  {!hasUniformPricing && (
                    <span className="text-sm text-muted-foreground">per item</span>
                  )}
                </div>

                {/* CTA Button */}
                <div className="space-y-3">
                  <Button
                    size="lg"
                    className="w-full md:w-auto text-lg px-8 py-6 bg-accent hover:bg-accent/90 text-white"
                    onClick={handleCreateDesign}
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Design My {product.name}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Design with AI in minutes. No design skills required.
                  </p>
                </div>

                {/* Features List */}
                <div className="pt-4 space-y-2">
                  <FeatureItem text="AI-powered design generation" />
                  <FeatureItem text="Unlimited iterations & refinements" />
                  <FeatureItem text="High-quality printing & materials" />
                  <FeatureItem text="Fast shipping & delivery" />
                </div>
              </div>
            </div>
          </section>

          {/* Example Images Section */}
          {product.exampleImages && product.exampleImages.length > 0 && (
            <section className="container mx-auto px-4 py-12 md:py-16">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-h2 font-bold mb-8 text-center">Example Designs</h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {product.exampleImages.map((imageUrl, index) => (
                    <div key={index} className="aspect-square rounded-lg overflow-hidden bg-muted">
                      <img
                        src={imageUrl}
                        alt={`${product.name} example ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* How It Works Section */}
          <section className="bg-muted/50 py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-h2 font-bold mb-8 text-center">How It Works</h2>
                <ProductProcessFlow />
              </div>
            </div>
          </section>

          {/* Variants Section */}
          {product.variants.length > 1 && (
            <section className="container mx-auto px-4 py-12 md:py-16">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-h2 font-bold mb-8 text-center">Available Options</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.variants.map((variant, index) => (
                    <Card key={index}>
                      <CardHeader>
                        <CardTitle className="text-lg">{variant.name}</CardTitle>
                        {variant.description && (
                          <CardDescription>{variant.description}</CardDescription>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold">
                            ${variant.stripePrice.toFixed(2)}
                          </span>
                        </div>
                        {variant.options && Object.keys(variant.options).length > 0 && (
                          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                            {Object.entries(variant.options).map(([key, value]) => (
                              <div key={key}>
                                <span className="capitalize">{key}:</span> {value}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Final CTA Section */}
          <section className="bg-primary text-primary-foreground py-12 md:py-16">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-h2 md:text-h1 font-bold mb-4">
                Ready to Create Your {product.name}?
              </h2>
              <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                Bring your ideas to life with AI-powered design
              </p>
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-6"
                onClick={handleCreateDesign}
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Design My {product.name}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      {/* Auth Dialog */}
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onOpenChange={setIsAuthDialogOpen}
        redirectTo={`/projects/new?product=${product.id}`}
      />
    </>
  );
}

// Helper Components
function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <Check className="h-5 w-5 text-primary flex-shrink-0" />
      <span className="text-sm">{text}</span>
    </div>
  );
}
